import { Component, OnInit, OnChanges, SimpleChanges, Input } from '@angular/core';

import { ToastrService } from 'ngx-toastr';
import {PaiementService } from '../../../../../../services/paiement/paiement.service';
import { TresorerieService } from '../../../../../../services/tresorerie/tresorerie.service';
import {Tresorerie} from '../../../../../../models/Tresorerie/Tresorerie';
import {Paiement} from '../../../../../../models/paiement/Paiement';
import {PaiementNonAffecte} from '../../../../../../models/paiement/PaiementNonAffecte';
import {LignePaiement} from '../../../../../../models/paiement/LignePaiement';

@Component({
  selector: 'app-paiement-non-aff',
  templateUrl: './paiement-non-aff.component.html',
  styleUrls: ['./paiement-non-aff.component.scss']
})
export class PaiementNonAffComponent implements OnInit, OnChanges {
    @Input() documentId: number | null = null;
    @Input() clientId: number | null = null;
    @Input() stationId: number | null = null;

    paiementsNonAffectes: PaiementNonAffecte[] = [];
    isLoading: boolean = false;

    constructor(
        private paiementService: PaiementService,
        private tresorerieService: TresorerieService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        // Si clientId est fourni, charger les avances en espèce depuis tresorerie
        if (this.clientId) {
            this.loadAvancesEspece();
        } else if (this.documentId) {
            // Sinon, charger les paiements non affectés si documentId est déjà disponible
            this.loadPaiementsNonAffectes(this.documentId);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Si clientId change, recharger les avances en espèce
        if (changes['clientId'] && !changes['clientId'].firstChange && this.clientId) {
            console.log('🔄 [ngOnChanges] clientId a changé, rechargement des avances en espèce:', this.clientId);
            this.loadAvancesEspece();
        }
        // Si documentId change, recharger les paiements non affectés
        else if (changes['documentId'] && !changes['documentId'].firstChange && this.documentId && !this.clientId) {
            console.log('🔄 [ngOnChanges] documentId a changé, rechargement des paiements non affectés:', this.documentId);
            this.loadPaiementsNonAffectes(this.documentId);
        }
    }

    /**
     * Charger les avances en espèce depuis la table tresorerie
     * Filtres: type_operation=avance_espece, client_id=clientId, statut=actif (ou valide)
     */
    loadAvancesEspece(): void {
        if (!this.clientId) {
            console.warn('⚠️ [loadAvancesEspece] clientId non fourni');
            return;
        }

        console.log('📋 [loadAvancesEspece] Chargement des avances en espèce pour client_id:', this.clientId);
        this.isLoading = true;
        this.paiementsNonAffectes = [];

        this.tresorerieService.getAvancesEspeceClient(this.clientId, this.stationId || undefined).subscribe({
            next: (operations: Tresorerie[]) => {
                console.log('📦 [loadAvancesEspece] Opérations tresorerie récupérées:', operations);
                this.processAvancesEspece(operations);
                this.isLoading = false;
            },
            error: (error) => {
                console.error('❌ [loadAvancesEspece] Erreur lors du chargement des avances en espèce:', error);
                this.toastr.error('Erreur lors du chargement des avances en espèce', 'Erreur');
                this.isLoading = false;
            }
        });
    }

    /**
     * Convertir les opérations tresorerie en format PaiementNonAffecte
     */
    private processAvancesEspece(operations: Tresorerie[]): void {
        this.paiementsNonAffectes = [];

        console.log('═══════════════════════════════════════════════════════════');
        console.log('🔄 [processAvancesEspece] TRAITEMENT DES AVANCES EN ESPÈCE');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📦 [processAvancesEspece] Nombre d\'opérations à traiter:', operations.length);

        for (const operation of operations) {
            // Vérifier les critères - le filtrage par client_id est CRITIQUE
            if (operation.type_operation !== 'avance_espece') {
                console.log('⚠️ [processAvancesEspece] Opération ignorée - type_operation incorrect:', operation.type_operation);
                continue;
            }
            if (operation.statut !== 'valide' && operation.statut !== 'actif') {
                console.log('⚠️ [processAvancesEspece] Opération ignorée - statut non valide/actif:', operation.statut);
                continue;
            }
            // Filtrer strictement par client_id - doit correspondre exactement au client de la facture
            if (this.clientId && operation.client_id !== this.clientId) {
                console.log('⚠️ [processAvancesEspece] Opération ignorée - client_id non correspondant:', operation.client_id, 'vs attendu:', this.clientId);
                continue;
            }

            // Formater le montant
            let montantFormate = '0.00';
            if (operation.montant) {
                const montantNum = parseFloat(operation.montant);
                if (!isNaN(montantNum)) {
                    montantFormate = montantNum.toFixed(2);
                } else {
                    montantFormate = operation.montant;
                }
            }

            // Convertir la date au format YYYY-MM-DD si elle est au format ISO complet
            let dateFormatee = operation.date_operation || operation.created_at || new Date().toISOString().split('T')[0];
            if (dateFormatee && dateFormatee.includes('T')) {
                dateFormatee = dateFormatee.split('T')[0];
            }

            // Convertir en PaiementNonAffecte
            const paiementNonAffecte: PaiementNonAffecte = {
                id: operation.id,
                paiement_id: operation.paiement_id || undefined,
                document_id: operation.document_id || undefined,
                date: dateFormatee,
                type: 'avance_espece',
                numero: operation.document_reference || `AV-${operation.id}` || '--',
                montant: montantFormate,
                reference: operation.document_reference || undefined,
                action: undefined,
                created_at: operation.created_at,
                updated_at: operation.updated_at
            };

            console.log('  ➕ Ajout de l\'avance en espèce:', {
                id: paiementNonAffecte.id,
                client_id: operation.client_id,
                montant: paiementNonAffecte.montant,
                date: paiementNonAffecte.date,
                numero: paiementNonAffecte.numero
            });

            this.paiementsNonAffectes.push(paiementNonAffecte);
        }

        console.log('═══════════════════════════════════════════════════════════');
        console.log('✅ [processAvancesEspece] RÉSULTAT FINAL');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📊 Total des avances en espèce trouvées:', this.paiementsNonAffectes.length);

        if (this.paiementsNonAffectes.length > 0) {
            console.log('📋 [processAvancesEspece] Liste complète des avances en espèce:');
            let totalMontant = 0;
            this.paiementsNonAffectes.forEach((pna, index) => {
                const montant = parseFloat(pna.montant || '0');
                totalMontant += montant;
                console.log(`  [${index + 1}] ID: ${pna.id}, Montant: ${pna.montant} TND, Date: ${pna.date}, Numéro: ${pna.numero}`);
            });
            console.log('💰 Total des montants:', totalMontant.toFixed(2), 'TND');
            this.toastr.success(`${this.paiementsNonAffectes.length} avance(s) en espèce trouvée(s)`, 'Information');
        } else {
            console.log('ℹ️ [processAvancesEspece] Aucune avance en espèce à afficher');
        }
        console.log('═══════════════════════════════════════════════════════════');
    }

    loadPaiementsNonAffectes(documentId: number): void {
        console.log('📋 [loadPaiementsNonAffectes] Chargement des paiements non affectés pour document_id:', documentId);
        this.documentId = documentId;
        this.isLoading = true;
        this.paiementsNonAffectes = [];

        // Récupérer tous les paiements pour ce document
        this.paiementService.getPaiementsByDocumentId(documentId).subscribe({
            next: (response: any) => {
                console.log('📦 [loadPaiementsNonAffectes] Réponse brute:', response);

                // Extraire les paiements de la réponse encapsulée
                let paiements: Paiement[] = [];
                if (Array.isArray(response)) {
                    paiements = response;
                } else if (response && response.data) {
                    if (Array.isArray(response.data)) {
                        paiements = response.data;
                    } else if (response.data.paiements && Array.isArray(response.data.paiements)) {
                        paiements = response.data.paiements;
                    } else if (response.data.paiement && typeof response.data.paiement === 'object') {
                        paiements = [response.data.paiement];
                    }
                } else if (response && response.id) {
                    paiements = [response];
                }

                console.log('📦 [loadPaiementsNonAffectes] Paiements extraits:', paiements);
                this.processPaiementsNonAffectes(paiements);
                this.isLoading = false;
            },
            error: (error) => {
                console.error('❌ [loadPaiementsNonAffectes] Erreur lors du chargement des paiements:', error);
                this.toastr.error('Erreur lors du chargement des paiements non affectés', 'Erreur');
                this.isLoading = false;
            }
        });
    }

    private processPaiementsNonAffectes(paiements: Paiement[]): void {
        this.paiementsNonAffectes = [];

        console.log('═══════════════════════════════════════════════════════════');
        console.log('🔄 [processPaiementsNonAffectes] TRAITEMENT DES PAIEMENTS NON AFFECTÉS');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📦 [processPaiementsNonAffectes] Nombre de paiements à traiter:', paiements.length);

        // Parcourir tous les paiements et extraire les paiements non affectés
        for (const paiement of paiements) {
            console.log('───────────────────────────────────────────────────────────');
            console.log('📋 [processPaiementsNonAffectes] Paiement ID:', paiement.id);
            console.log('   📄 document_id:', paiement.document_id);
            console.log('   💰 net_a_payer:', paiement.net_a_payer);
            console.log('   📊 nombre de lignes_paiement:', paiement.lignes_paiement?.length || 0);
            console.log('   ⚠️ nombre de paiements_non_affectes:', paiement.paiements_non_affectes?.length || 0);

            // Le backend retourne déjà les paiements_non_affectes dans le modèle Paiement
            if (paiement.paiements_non_affectes && paiement.paiements_non_affectes.length > 0) {
                console.log('✅ [processPaiementsNonAffectes] Paiement ID', paiement.id, 'contient', paiement.paiements_non_affectes.length, 'paiement(s) non affecté(s)');

                for (const pna of paiement.paiements_non_affectes) {
                    // S'assurer que le montant est correctement formaté (2 décimales)
                    let montantFormate = '0.00';
                    if (pna.montant) {
                        const montantNum = parseFloat(pna.montant);
                        if (!isNaN(montantNum)) {
                            montantFormate = montantNum.toFixed(2);
                        } else {
                            montantFormate = pna.montant;
                        }
                    }
                    
                    // Convertir le PaiementNonAffecte du backend en format d'affichage
                    const paiementNonAffecte: PaiementNonAffecte = {
                        id: pna.id,
                        paiement_id: pna.paiement_id || paiement.id,
                        document_id: pna.document_id || paiement.document_id || this.documentId || undefined,
                        date: pna.date || new Date().toISOString().split('T')[0],
                        type: pna.type || 'Non spécifié',
                        numero: pna.numero || pna.reference || '--',
                        montant: montantFormate,
                        reference: pna.reference,
                        action: pna.action,
                        created_at: pna.created_at,
                        updated_at: pna.updated_at
                    };

                    console.log('  ➕ Ajout du paiement non affecté:', {
                        id: paiementNonAffecte.id,
                        paiement_id: paiementNonAffecte.paiement_id,
                        type: paiementNonAffecte.type,
                        montant: paiementNonAffecte.montant,
                        montant_original: pna.montant,
                        montant_formate: montantFormate,
                        numero: paiementNonAffecte.numero,
                        date: paiementNonAffecte.date
                    });
                    console.log('  💰 [processPaiementsNonAffectes] Montant préservé:', montantFormate, 'TND (original:', pna.montant, ')');

                    this.paiementsNonAffectes.push(paiementNonAffecte);
                }
            } else {
                console.log('ℹ️ [processPaiementsNonAffectes] Paiement ID', paiement.id, 'n\'a pas de paiements non affectés');
            }
        }

        console.log('═══════════════════════════════════════════════════════════');
        console.log('✅ [processPaiementsNonAffectes] RÉSULTAT FINAL');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📊 Total des paiements non affectés trouvés:', this.paiementsNonAffectes.length);

        if (this.paiementsNonAffectes.length > 0) {
            console.log('📋 [processPaiementsNonAffectes] Liste complète des paiements non affectés:');
            let totalMontant = 0;
            this.paiementsNonAffectes.forEach((pna, index) => {
                const montant = parseFloat(pna.montant || '0');
                totalMontant += montant;
                console.log(`  [${index + 1}] ID: ${pna.id}, Paiement ID: ${pna.paiement_id}, Type: ${pna.type}, Montant: ${pna.montant} TND, Numéro: ${pna.numero}, Date: ${pna.date}`);
            });
            console.log('💰 Total des montants non affectés:', totalMontant.toFixed(2), 'TND');
            this.toastr.success(`${this.paiementsNonAffectes.length} paiement(s) non affecté(s) trouvé(s)`, 'Information');
        } else {
            console.log('ℹ️ [processPaiementsNonAffectes] Aucun paiement non affecté à afficher');
        }
        console.log('═══════════════════════════════════════════════════════════');
    }

    getTypePaiementLabel(type: string | undefined): string {
        if (!type) return 'Non spécifié';
        const labels: { [key: string]: string } = {
            'espece': 'Espèce',
            'cheque': 'Chèque',
            'virement': 'Virement',
            'traite': 'Traite',
            'avance_espece': 'Avance en espèce',
            'Reste à payer': 'Reste à payer'
        };
        return labels[type] || type;
    }

    formatDate(date: string | undefined): string {
        if (!date) return '--';
        try {
            // Si la date est au format ISO (YYYY-MM-DD ou avec heure)
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) {
                // Si ce n'est pas une date valide, retourner la string telle quelle
                return date;
            }
            // Formater en DD/MM/YYYY
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (error) {
            console.error('Erreur de formatage de date:', error);
            return date;
        }
    }

    trackByPaiementId(index: number, paiement: PaiementNonAffecte): any {
        return paiement.id || index;
    }

    /**
     * Formate le montant pour l'affichage
     * Garantit que le montant affiché correspond exactement au montant calculé lors de la création
     */
    formatMontant(montant: string | undefined): number {
        if (!montant) return 0;
        const montantNum = parseFloat(montant);
        return isNaN(montantNum) ? 0 : montantNum;
    }

    affecterPaiement(paiement: PaiementNonAffecte): void {
        // Logique pour affecter le paiement à la facture
        // Cette méthode peut être implémentée selon vos besoins
        console.log('🔄 [affecterPaiement] Affectation du paiement non affecté:', paiement);
        this.toastr.info('Fonctionnalité d\'affectation à implémenter', 'Information');
    }
}
